import { unionBy, uniqBy } from "lodash-es";
import pluralize from "pluralize";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { Item, MenuOptionItem, MenuTriggerButton, Section, TriggerButton, TriggerButtonIntent } from "widgets";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { getSavedItemTypeLabel, pluralizeWord } from "@/app-utils/string.js";
import type { CoordinateList, Group, SavedItem, SavedItemsListing, User } from "@/graphql.js";
import { checkCapability } from "@/modules/users/utils/user.js";
import { getProjectLabel } from "@/redux/modules/config.js";
import { deleteFlags } from "@/redux/modules/flag.ts";
import { deleteCoordinateLists } from "@/redux/modules/nuFeature.ts";
import { deleteParents } from "@/redux/modules/parent.ts";
import { deleteGroups, viewUnion, viewUnionOfSamplesFromParents } from "@/redux/modules/saved-group.ts";
import { deleteViews } from "@/redux/modules/saved-view.ts";
import type { IAppState } from "@/redux/reducer.js";
import { CAPABILITY, ENTITY_TYPE } from "@/settings/settings.ts";
import DeleteSavedItemConfirmationDialog from "@/ui-modules/admin/DeleteSavedItemConfirmationDialog.tsx";
import {
  ModalActionWidget,
  ModalControlledOverlay,
  PopOverActionWidget,
} from "@/ui-modules/core/overlays/components/Overlays.tsx";
import { SavedItemReportPlaybooksButton } from "@/ui-modules/core/SavedItemReportPlaybooksButton.tsx";
import VennDiagramDialog from "@/ui-modules/core/VennDiagramDialog.tsx";
import { CoordinateListUploadButton } from "@/ui-modules/entities/CoordinatesListUpload.tsx";
import { CopyItemsDialog } from "@/ui-modules/entities/CopyItemsDialog.tsx";

/*--- Redux --- */
interface IItemListTabToolbarAppStateProps {
  user: User;
}

function mapStateToProps(state: IAppState): IItemListTabToolbarAppStateProps {
  return {
    user: state.auth.session,
  };
}

interface IItemListTabToolbarDispatchProps {
  deleteGroups?: (groupIds: number[], groupType: string) => Promise<boolean>;
  deleteViews?: (viewIds: number[]) => Promise<boolean>;
  deleteFlags?: (flagIds: number[]) => Promise<boolean>;
  deleteCoordinateLists?: (listIds: number[]) => Promise<boolean>;
  deleteParents?: (parentIds: number[], parentTypeName: string) => Promise<boolean>;
  onViewUnion?: (groupIds: number[]) => Promise<boolean>;
  onViewUnionOfSamplesFromParents?: (parentIds: number[]) => Promise<boolean>;
}

function mapDispatchToProps(dispatch): IItemListTabToolbarDispatchProps {
  return {
    deleteGroups: (groupIds: number[], groupType: string) => dispatch(deleteGroups(groupIds, groupType)),
    deleteViews: (viewIds: number[]) => dispatch(deleteViews(viewIds)),
    deleteFlags: (flagIds: number[]) => dispatch(deleteFlags(flagIds)),
    deleteCoordinateLists: (coordinateListIds: number[]) => dispatch(deleteCoordinateLists(coordinateListIds)),
    deleteParents: (parentIds: number[], parentTypeName: string) => dispatch(deleteParents(parentIds, parentTypeName)),
    onViewUnion: (groupIds: number[]) => dispatch(viewUnion(groupIds)),
    onViewUnionOfSamplesFromParents: (parentIds: number[]) => dispatch(viewUnionOfSamplesFromParents(parentIds)),
  };
}

export interface IItemListTabToolbarProps {
  itemsData: SavedItemsListing;
  items?: SavedItem[];
  selectedIds: number[];
  projectId: number;

  onRefreshNeeded: () => void;
}

interface IItemListTabToolbarState {
  showCopyDialog?: boolean;
  showCompareDialog?: boolean;
}

/**
 * Toolbar of list of items for hosting action button and search field.
 */
class ItemListTabToolbar extends PureComponent<
  IItemListTabToolbarProps & IItemListTabToolbarAppStateProps & IItemListTabToolbarDispatchProps,
  IItemListTabToolbarState
> {
  state = {
    showCopyDialog: false,
    showCompareDialog: false,
  };

  hasSelectedItems = (): boolean => {
    return this.props.selectedIds.length !== 0;
  };

  hasExactlyOneSelectedItem = (): boolean => {
    return this.props.selectedIds.length === 1;
  };

  selectedItemsHaveAllSameSubtype = (): boolean => {
    const subtypes: SavedItem[] = uniqBy(this.getSelectedSavedItems(), "typeDisplayName");
    return subtypes.length <= 1;
  };

  getSelectedSavedItems = (): SavedItem[] => {
    return this.props.selectedIds
      .map((selectedId) => this.props.items?.find((item: SavedItem) => item.id === selectedId))
      .filter((value) => !!value) as SavedItem[];
  };

  hasDeletePermissions = (): boolean => {
    if (
      this.props.itemsData.type === ENTITY_TYPE.SAMPLE_PARENT &&
      checkCapability(this.props.user.capabilities, CAPABILITY.DELETE_SAMPLES)
    ) {
      return true;
    }

    // Check for each member of the bucket, if it is deletable.
    for (const selectedId of this.props.selectedIds) {
      const selectedSavedItem: SavedItem = this.props.items?.find((item: SavedItem) => item.id === selectedId);
      if (!selectedSavedItem?.deletable) {
        return false;
      }
    }
    return true;
  };

  getCopyButtonDisabledTooltip(): string {
    const itemsData: SavedItemsListing = this.props.itemsData;

    // If the selection is empty, the delete button remains disabled.
    if (!this.hasSelectedItems()) {
      return `At least one ${getSavedItemTypeLabel(itemsData)} must be selected to copy to a ${getProjectLabel()}`;
    }
    return null;
  }

  getDeleteButtonDisabledTooltip = (): string => {
    const itemsData: SavedItemsListing = this.props.itemsData;
    const typeLabel = getSavedItemTypeLabel(itemsData);

    // If the selection is empty, the delete button remains disabled.
    if (!this.hasSelectedItems()) {
      return `At least one ${typeLabel} must be selected to perform a deletion`;
    }
    // If one selected item is not deletable
    if (!this.hasDeletePermissions()) {
      return `You do not have permission to delete one or more of the ${pluralize(typeLabel)} you have selected`;
    }
    return null;
  };

  toggleDialog = (field: string, show?: boolean) => {
    if (typeof show === "boolean") {
      this.setState({ [field]: show });
    } else {
      this.setState((prevState: IItemListTabToolbarState) => {
        return { [field]: !prevState[field] };
      });
    }
  };

  confirmDeletion = () => {
    const selectedIds = this.props.selectedIds;
    const itemsData = this.props.itemsData;

    if (itemsData.type === ENTITY_TYPE.VIEW) {
      return this.props.deleteViews?.(selectedIds);
    } else if (itemsData.type === ENTITY_TYPE.REGION_FLAG) {
      return this.props.deleteFlags?.(selectedIds);
    } else if (itemsData.type === ENTITY_TYPE.COORD_LIST) {
      return this.props.deleteCoordinateLists?.(selectedIds);
    } else if (itemsData.type === ENTITY_TYPE.SAMPLE_PARENT) {
      return this.props.deleteParents?.(selectedIds, itemsData.name);
    } else {
      return this.props.deleteGroups?.(selectedIds, itemsData.type);
    }
  };

  buildCompareModalDialog() {
    const itemsData: SavedItemsListing = this.props.itemsData;
    let comparisonGroups: Group[] = this.props.items as Group[];

    // make sure we suggest only comparable groups
    if (
      itemsData.subType === ENTITY_TYPE.MARKER ||
      itemsData.subType === ENTITY_TYPE.NUFEATURE ||
      itemsData.subType === ENTITY_TYPE.AAFEATURE
    ) {
      const selectedSavedItems = this.getSelectedSavedItems();
      const selectedSubtype = selectedSavedItems.length && selectedSavedItems[0].typeDisplayName;
      comparisonGroups = (this.props.items as Group[]).filter(
        (group: Group) => group.typeDisplayName === selectedSubtype,
      );
    }

    return this.state.showCompareDialog ? (
      <ModalControlledOverlay close={() => this.toggleDialog("showCompareDialog")} isOpen={true}>
        {({ close }) => (
          <VennDiagramDialog
            type={itemsData.subType}
            onCancel={close}
            onApply={close}
            items={comparisonGroups}
            selectedItems={this.props.selectedIds.length ? this.props.selectedIds : undefined}
          />
        )}
      </ModalControlledOverlay>
    ) : null;
  }

  getAnalyzeButtonDisabledTooltip = (): string | undefined => {
    const selectedIds = this.props.selectedIds;
    const selectedCoordLists: CoordinateList[] = (this.props.items as CoordinateList[]).filter((item) =>
      selectedIds.includes(item.id),
    );

    const hasOrganism = selectedCoordLists.some((list) => list.organism != undefined);

    const organisms = unionBy(
      selectedCoordLists.filter((l) => l.organism != undefined).map((list) => list.organism),
      "id",
    );

    if (!hasOrganism) {
      return `The organism of the selected coordinate ${pluralize("list", selectedIds.length)} is not defined.`;
    } else if (selectedIds.length < 1) {
      return "Select at least one coordinate lists to enable this button.";
    } else if (organisms.length > 1) {
      return "Your selection of coordinate lists should all be related to the same organism.";
    }
    return undefined;
  };

  // @ts-ignore
  getCompareButtonDisabledTooltip = () => {
    const itemsData: SavedItemsListing = this.props.itemsData;

    // for variants the compare dialog can only be opened with exactly one group selected,
    // because this group determines the set of reference genomes and compatible groups (via analysis group)
    if (itemsData.subType === ENTITY_TYPE.MUTATION && !this.hasExactlyOneSelectedItem()) {
      return "Exactly one variant group must be selected to perform a comparison.";
    }

    if (
      itemsData.subType === ENTITY_TYPE.MARKER ||
      itemsData.subType === ENTITY_TYPE.NUFEATURE ||
      itemsData.subType === ENTITY_TYPE.AAFEATURE
    ) {
      if (!this.selectedItemsHaveAllSameSubtype()) {
        return "Only groups of the same kind must be selected to perform a comparison.";
      }
      // we need to have at least one group selected in order to show only groups of the same subtype in the venn diagram dialog
      if (!this.hasSelectedItems()) {
        return "At least one group must be selected to perform a comparison.";
      }
    }
  };

  getConfirmMessage = () => {
    if (this.props.itemsData.type == ENTITY_TYPE.SAMPLE_PARENT) {
      const find = this.getSelectedSavedItems()
        .flatMap((item) => item.samples)
        .filter((sample) => sample?.organism);

      return find?.length
        ? `${find?.length} ${pluralizeWord("sample", find?.length)} ${pluralizeWord(
            "is",
            find?.length,
          )} linked to genomic content in the database. Do you want to proceed?`
        : null;
    }
    return null;
  };

  getViewButtonDisabledTooltip = () => {
    const itemsData: SavedItemsListing = this.props.itemsData;
    const typeLabel: string = pluralize.plural(getSavedItemTypeLabel(itemsData));

    const unionLabel: string = itemsData.type === ENTITY_TYPE.SAMPLE_PARENT ? `union of all samples` : "union";
    // If the selection is < 1, the button remains disabled.
    if (this.props.selectedIds.length < 1) {
      return `Multiple ${typeLabel} must be selected to view the ${unionLabel}`;
    }
    if (
      itemsData.subType === ENTITY_TYPE.MARKER ||
      itemsData.subType === ENTITY_TYPE.NUFEATURE ||
      itemsData.subType === ENTITY_TYPE.AAFEATURE
    ) {
      if (!this.selectedItemsHaveAllSameSubtype()) {
        return `Only groups of the same kind must be selected to view the ${unionLabel}`;
      }
    }
    return null;
  };

  onViewUnion = () => {
    if (this.props.itemsData.type === ENTITY_TYPE.SAMPLE_PARENT) {
      return this.props.onViewUnionOfSamplesFromParents(this.props.selectedIds);
    }
    return this.props.onViewUnion(this.props.selectedIds);
  };

  render(): ReactElement {
    const props = this.props;
    const itemsData: SavedItemsListing = this.props.itemsData;
    const itemType: string = itemsData.type;
    const itemSubtype: string = itemsData.subType;
    const typeLabel: string = pluralizeWord(getSavedItemTypeLabel(itemsData), props.selectedIds.length);

    const showDeleteBtn: boolean =
      (itemType !== ENTITY_TYPE.SAMPLE_PARENT && itemType !== ENTITY_TYPE.ANALYST_SESSION) ||
      (itemType === ENTITY_TYPE.SAMPLE_PARENT &&
        checkCapability(this.props.user.capabilities, CAPABILITY.DELETE_SAMPLES));
    const showCopyBtn: boolean =
      itemType !== ENTITY_TYPE.SAMPLE_PARENT &&
      itemType !== ENTITY_TYPE.ANALYST_SESSION &&
      itemType !== ENTITY_TYPE.COORD_LIST;
    const showCompareBtn: boolean =
      itemType !== ENTITY_TYPE.REGION_FLAG &&
      itemType !== ENTITY_TYPE.VIEW &&
      itemType !== ENTITY_TYPE.SAMPLE_PARENT &&
      itemType !== ENTITY_TYPE.ANALYST_SESSION &&
      itemType !== ENTITY_TYPE.COORD_LIST;

    const canCompareDisabledTooltip: string = this.getCompareButtonDisabledTooltip();
    const showViewBtn: boolean =
      itemType !== ENTITY_TYPE.REGION_FLAG &&
      itemType !== ENTITY_TYPE.VIEW &&
      itemSubtype !== ENTITY_TYPE.MUTATION &&
      itemType !== ENTITY_TYPE.ANALYST_SESSION &&
      itemType !== ENTITY_TYPE.COORD_LIST;
    const canViewMultipleDisabledTooltip: string = this.getViewButtonDisabledTooltip();

    const showCoordinatesListUploadBtn: boolean = itemType === ENTITY_TYPE.COORD_LIST;

    const showReportButton: boolean = itemSubtype === ENTITY_TYPE.VCF_VARIANT || itemType === ENTITY_TYPE.COORD_LIST;

    const canAnalyzeCoordListsDisabledTooltip: string = this.getAnalyzeButtonDisabledTooltip();
    const showAnalyzeCoordListsBtn: boolean = itemType === ENTITY_TYPE.COORD_LIST;

    return (
      <>
        {showCoordinatesListUploadBtn && (
          <CoordinateListUploadButton
            key={"coord-list-upload-button"}
            defaultProjectId={props.projectId}
            onUploadSuceeded={this.props.onRefreshNeeded}
          />
        )}
        {showReportButton && (
          <SavedItemReportPlaybooksButton
            key={"report-playbooks-button"}
            items={props.items}
            itemsData={itemsData}
            selectedIds={props.selectedIds}
          />
        )}
        {showAnalyzeCoordListsBtn && (
          <MenuTriggerButton
            key={"analyze-coord-list"}
            icon={"query_stats"}
            label={"Analyze"}
            tooltip={"Analyze"}
            isDisabled={canAnalyzeCoordListsDisabledTooltip}
            onAction={(key) => {
              switch (key) {
                case "send-to-genome-browser":
                  {
                    const selectedIds = props.selectedIds;
                    const selectedCoordLists: CoordinateList[] = (props.items as CoordinateList[]).filter((item) =>
                      selectedIds.includes(item.id),
                    );
                    window.open(
                      BrowserTab.createUrl(
                        `/organism/${selectedCoordLists[0]?.organism?.id}/genome?add_tracks=${
                          ENTITY_TYPE.COORDINATE
                        }&coordLists=${selectedIds.join(",")}&showGrid=true`,
                      ),
                    );
                  }
                  break;
              }
            }}
          >
            <Section title={"Show selected lists in"}>
              <Item key={"send-to-genome-browser"}>
                <MenuOptionItem title={"Genome Viewer"} id={"send-to-genome-browser"} />
              </Item>
            </Section>
          </MenuTriggerButton>
        )}
        {showCompareBtn && (
          <>
            <TriggerButton
              key={"item-list-tab-toolbar-action-1"}
              isDisabled={canCompareDisabledTooltip}
              icon={"compare"}
              tooltip={"Compare two groups"}
              onPress={() => this.toggleDialog("showCompareDialog")}
              label={"Compare"}
            />
            {this.buildCompareModalDialog()}
          </>
        )}
        {showViewBtn && (
          <TriggerButton
            key={"item-list-tab-toolbar-action-2"}
            isDisabled={canViewMultipleDisabledTooltip}
            icon={"table"}
            tooltip={`View ${
              itemsData.type === ENTITY_TYPE.SAMPLE_PARENT ? `union of all samples` : "union"
            } of your selection`}
            onPress={this.onViewUnion}
            label={"View"}
          />
        )}
        {showCopyBtn && (
          <PopOverActionWidget
            title={"Copy"}
            actionWidget={({ open }) => (
              <TriggerButton
                icon={"content_copy"}
                tooltip={`Copy the selected ${typeLabel} to a ${getProjectLabel(false, false)}`}
                onPress={() => open()}
                isDisabled={this.getCopyButtonDisabledTooltip()}
                label={"Copy"}
              />
            )}
          >
            {({ close }) => (
              <CopyItemsDialog
                items={this.props.items}
                itemsData={itemsData}
                selectedIds={this.props.selectedIds}
                projectId={this.props.projectId}
                onApply={() => {
                  close();
                  this.props.onRefreshNeeded();
                }}
                onCancel={close}
              />
            )}
          </PopOverActionWidget>
        )}
        {showDeleteBtn && (
          <ModalActionWidget
            actionWidget={({ open }) => (
              <TriggerButton
                id={"item-list-tab-toolbar-action-4"}
                tooltip={`Delete the selected ${typeLabel}`}
                onPress={() => open()}
                isDisabled={this.getDeleteButtonDisabledTooltip()}
                intent={TriggerButtonIntent.Danger}
                icon={"delete"}
                label={"Delete"}
              />
            )}
          >
            {({ close }) => (
              <DeleteSavedItemConfirmationDialog
                itemsData={this.props.itemsData}
                selectedIds={this.props.selectedIds}
                confirmCallback={() =>
                  this.confirmDeletion().then(() => {
                    close();
                    this.props.onRefreshNeeded();
                  })
                }
                cancelCallback={() => close()}
                key={"item-list-tab-toolbar-action-5"}
                confirmationMessage={this.getConfirmMessage()}
              />
            )}
          </ModalActionWidget>
        )}
      </>
    );
  }
}

const ConnectedItemListTabToolbar = connect<IItemListTabToolbarAppStateProps, IItemListTabToolbarDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(ItemListTabToolbar);

export default ConnectedItemListTabToolbar;
